/** What a plan change costs, shown before it is agreed to.
 *
 * A month is paid in advance, so an upgrade halfway through one is priced
 * against the time already bought — the difference in rate, for the hours
 * remaining. That is the fair way to charge it and an impossible number to
 * guess, which is exactly why it has to be on screen before the button
 * rather than on the statement afterwards.
 *
 * The figure comes from the server, from the same function that does the
 * charging. Computing it here from the plan list would be easy and would
 * eventually disagree — and a customer shown one number and billed another
 * has been lied to, however small the gap.
 */

import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { api } from '../lib/api'
import { topUpLink } from '../lib/billingFlow'
import { money } from '../lib/format'
import type { PlanQuote as Quote } from '../lib/types'

type Props = {
  /** Which resource, and which plan is being considered. */
  kind: 'service' | 'database' | 'project'
  id: string
  plan: string
  /** The plan it is on now: no quote is needed for staying put. */
  current: string
  allowCurrent?: boolean
  onQuote?: (quote: Quote | null) => void
}

export default function PlanQuote({ kind, id, plan, current, onQuote, allowCurrent = false }: Props) {
  const location = useLocation()
  const [quote, setQuote] = useState<Quote | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  useEffect(() => {
    onQuote?.(null)
    if (plan === current && !allowCurrent) {
      setQuote(null)
      setFailed(null)
      return
    }
    let live = true
    setQuote(null)
    setFailed(null)
    const ask =
      kind === 'service'
        ? api.servicePlanQuote
        : kind === 'project'
          ? api.projectPlanQuote
          : api.databasePlanQuote
    ask(id, plan)
      .then((result) => { if (live) { setQuote(result); onQuote?.(result) } })
      .catch((error) => live && setFailed(String(error?.message ?? error)))
    // A quote is only about this resource and this plan; nothing else it
    // could depend on can change without one of those changing.
    return () => {
      live = false
    }
  }, [kind, id, plan, current, onQuote, allowCurrent])

  if (plan === current && !allowCurrent) return null
  if (failed) return <p className="tiny danger">{failed}</p>
  if (!quote) {
    return (
      <p className="tiny faint" aria-live="polite">
        Working out what this costs…
      </p>
    )
  }

  const shortfall = quote.shortfall ?? String(Math.max(0, Number(quote.amount_due) - Number(quote.balance)))
  const returnPath = location.pathname === '/billing' ? `/billing?upgrade=${encodeURIComponent(id)}&plan=${encodeURIComponent(plan)}` : location.pathname + location.search
  const addFunds = topUpLink(quote.currency ?? 'ETB', shortfall, returnPath)
  const days = Math.floor(Number(quote.hours_remaining) / 24)
  const hours = Math.round(Number(quote.hours_remaining) % 24)
  const left =
    days > 0 ? `${days} ${days === 1 ? 'day' : 'days'}` : `${hours} hours`

  if (Number(quote.reservation_required ?? 0) > 0) {
    return <div className={`alert ${quote.affordable ? 'info' : 'warn'}`} aria-live="polite">
      <strong>{money(quote.reservation_required!, quote.currency ?? 'ETB')} wallet credit required.</strong> This reserves the first month; it is charged when the application starts successfully, not again for each component.
      {!quote.affordable && <p>Add {money(quote.shortfall ?? quote.reservation_required!, quote.currency ?? 'ETB')} to cover the reservation. <Link to={addFunds}>Add money to continue</Link></p>}
    </div>
  }
  if (quote.trial_covered) {
    return <div className="alert info" aria-live="polite">Included during your current trial. After it ends, this plan costs <strong>{money(quote.new_monthly, quote.currency ?? 'ETB')}/month</strong>. Changing plans does not restart your trial.</div>
  }
  if (quote.is_downgrade) {
    return (
      <div className="alert info" aria-live="polite">
        <strong>{money(quote.new_monthly, quote.currency ?? 'ETB')} a month</strong> from your next
        renewal, down from {money(quote.current_monthly, quote.currency ?? 'ETB')}. Nothing is charged
        now and nothing comes back — this resource has been running on the
        larger plan for the part of the month that has already passed.
      </div>
    )
  }

  if (Number(quote.amount_due) === 0) {
    return (
      <div className="alert info" aria-live="polite">
        No additional charge now. The next renewal uses the new rate of
        {' '}<strong>{money(quote.new_monthly, quote.currency ?? 'ETB')}/month</strong>.
      </div>
    )
  }

  return (
    <div
      className={`alert ${quote.affordable ? 'trial' : 'warn'}`}
      aria-live="polite"
    >
      <strong>{money(quote.amount_due, quote.currency ?? 'ETB')} now</strong> — the difference between{' '}
      {money(quote.current_monthly, quote.currency ?? 'ETB')} and {money(quote.new_monthly, quote.currency ?? 'ETB')} a month, for
      the {left} left on the month you have already paid for. Your renewal date
      does not move.
      <p>Paid from your {quote.currency ?? 'ETB'} wallet · Available: {money(quote.balance, quote.currency ?? 'ETB')}</p>
      {!quote.affordable && (
        <>
          {' '}
          <Link to={addFunds}>Add {money(shortfall, quote.currency ?? 'ETB')} to continue</Link>
        </>
      )}
    </div>
  )
}
